import { NO_VALUE } from "@/lib/format/metric";
import type { Language } from "@/i18n";

/**
 * The currencies a cost can be recorded in, in picker order. Upstream validates against the same
 * list (`docs/USER_PREFERENCES_SERVER.md`).
 */
export const CURRENCIES = ["EUR", "BAM", "RSD", "USD"] as const;

export type CurrencyCode = (typeof CURRENCIES)[number];

export function isCurrencyCode(value: unknown): value is CurrencyCode {
  return typeof value === "string" && (CURRENCIES as readonly string[]).includes(value);
}

/**
 * The fallback when neither the record nor the user's preference names one — before the
 * preference has loaded, or while upstream has no `UserPreferences.php` yet. New records take the
 * user's own default instead (`useDefaultCurrency`).
 */
export const DEFAULT_CURRENCY: CurrencyCode = "BAM";

/**
 * `Intl` locale tags for the four app languages, so amounts group and separate the way each
 * locale writes them (`1.234,56` in the regional locales, `1,234.56` in English).
 */
const MONEY_LOCALES: Record<Language, string> = {
  en: "en-US",
  sr: "sr-Latn-BA",
  bs: "bs-BA",
  hr: "hr-HR",
};

/**
 * An amount with its currency, always to two decimals.
 *
 * Unlike `formatMetric`, **zero is a real value here** and prints as `0,00 KM`. A free warranty
 * service is a fact worth recording, and blanking it would hide a cost the user deliberately
 * entered. Only a missing or unparseable amount falls back to the dash.
 */
export function formatMoney(
  amount: number | null | undefined,
  currency: string = DEFAULT_CURRENCY,
  language: Language = "sr",
): string {
  if (amount == null || !Number.isFinite(amount)) return NO_VALUE;

  const locale = MONEY_LOCALES[language] ?? MONEY_LOCALES.sr;
  const code = currency?.trim() || DEFAULT_CURRENCY;

  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    // An unrecognised ISO code makes Intl throw rather than degrade. Better a readable
    // `145.50 XYZ` than a crashed table cell.
    return `${amount.toFixed(2)} ${code}`;
  }
}

/** "EUR (€)", "BAM (KM)" — the code plus the symbol the locale writes, for pickers. */
export function currencyLabel(code: string, language: Language = "sr"): string {
  try {
    const symbol = new Intl.NumberFormat(MONEY_LOCALES[language] ?? MONEY_LOCALES.sr, {
      style: "currency",
      currency: code,
      currencyDisplay: "narrowSymbol",
    })
      .formatToParts(0)
      .find((part) => part.type === "currency")?.value;
    return symbol && symbol !== code ? `${code} (${symbol})` : code;
  } catch {
    return code;
  }
}

/**
 * The bare number, two decimals, no currency — for spreadsheet cells, which must stay numeric so
 * the sheet still sums and sorts. Mirrors how the metric exports keep raw values.
 */
export function moneyExportValue(amount: number | null | undefined): number | null {
  if (amount == null || !Number.isFinite(amount)) return null;
  return Math.round(amount * 100) / 100;
}

/**
 * Cost per kilometre, to two decimals, or `undefined` when either side is missing.
 *
 * Undefined rather than zero for the same reason as `consumptionPer100Km`: a period with no
 * distance driven has no cost-per-km to state, and dividing by it would read as a measurement.
 */
export function costPerKm(
  amount: number | null | undefined,
  distanceKm: number | null | undefined,
): number | undefined {
  if (amount == null || distanceKm == null) return undefined;
  if (!Number.isFinite(amount) || !Number.isFinite(distanceKm)) return undefined;
  if (distanceKm <= 0) return undefined;
  return Math.round((amount / distanceKm) * 100) / 100;
}
